// One test case, in full — the same modal for the tester, the PM and the developer.
//
// This reads as a TEST CASE first and a run second, which is the way round people
// actually use it. The previous version was built around the run: it showed a
// status chip, the instruction, an id, and — for anything not yet executed — the
// single line "This case hasn't been executed yet". That is the state most cases
// are in, so the modal was usually empty of the very thing you opened it for.
//
// What a case IS: a name, what must be true before you start, the steps, the data
// to use, and what should happen. Those live on the generated case and are
// fetched on open (the list endpoint only carries six fields per row, because it
// pages through thousands).
//
// What a case DID — steps executed, screenshots, the bug — is appended when the
// case has actually been run.

import { useCallback, useEffect, useState } from "react";
import {
  Loader2, Globe, ListChecks, Target, Hash, RotateCw, User as UserIcon,
  ClipboardList, Database, CheckCircle2, Hand, ShieldAlert, AlertTriangle,
  Image as ImageIcon, Bug,
} from "lucide-react";
import Modal from "@/components/dashboard/Modal";
import Button from "@/components/ui/Button";
import { Badge, StatusBadge, SeverityTag } from "@/components/ui/Badge";
import StepTimeline from "@/components/shared/StepTimeline";
import BugReportCard from "@/components/shared/BugReportCard";
import ScreenshotViewer from "@/components/shared/ScreenshotViewer";
import { normalizeSteps, failedSteps } from "@/lib/runSteps";
import { getExeRun } from "@/api/exe";
import { getExtRun } from "@/api/extension";
import { getRun } from "@/api/runs";
import { getProjectTestCase } from "@/api/projects";
import { cn } from "@/lib/cn";

// Which endpoint owns this run. The three runners store runs in separate tables,
// so the caller has to say which one — guessing would 404 two times out of three.
const FETCH_BY_KIND = { exe: getExeRun, extension: getExtRun, cloud: getRun };

/** A titled block that disappears when it has nothing to say.
 *
 * `show` is explicit rather than inferred from `children`. Inferring it doesn't
 * work: children is a React element (`<Bullets …/>`), which is always truthy
 * even when the component itself renders null — so an absent field printed a
 * bare heading with a gap under it. Plenty of fields are legitimately empty (a
 * navigation test needs no test data), so that was the common case, not the edge.
 *
 * Pass `empty` to keep the section visible with a note instead of hiding it —
 * for the fields where absence is worth remarking on.
 */
function Section({ icon: Icon, title, show, empty, children }) {
  if (!show && !empty) return null;
  return (
    <section>
      <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
        <Icon size={13} /> {title}
      </h4>
      {show ? children : <p className="text-sm text-slate-400">{empty}</p>}
    </section>
  );
}

/** Split a runner's free-text field into the list the generated case stores.
 *
 * "Send to Exe" flattens preconditions and test data into one text blob, so a
 * report opened from a batch had them as an unbroken paragraph — or, before the
 * serializer carried them at all, not at all. Numbered and bulleted forms both
 * appear in practice, hence the two prefixes. */
function lines(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || "")
    .split(/\r?\n|(?=\s*\d+[.)]\s)/)
    .map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").trim())
    .filter(Boolean);
}

function Bullets({ items, numbered = false }) {
  if (!items?.length) return null;
  const Tag_ = numbered ? "ol" : "ul";
  return (
    <Tag_ className="space-y-1.5">
      {items.map((line, i) => (
        <li key={i} className="flex gap-2.5 text-sm text-slate-700 dark:text-slate-200">
          <span className={cn(
            "mt-0.5 grid shrink-0 place-items-center rounded text-[0.6875rem] font-semibold",
            numbered
              ? "h-5 w-5 rounded-full bg-brand-500/10 text-brand-600 dark:text-brand-300"
              : "h-5 w-5 text-slate-300 dark:text-slate-600")}>
            {numbered ? i + 1 : "•"}
          </span>
          <span className="min-w-0 flex-1">{line}</span>
        </li>
      ))}
    </Tag_>
  );
}

/**
 * @param {object|null} testCase   the row that was clicked; null closes the modal
 * @param {string}      kind       "exe" | "extension" | "cloud" — which runner
 * @param {string}      projectId  when set, fetch the full generated-case detail
 * @param {function}    onClose
 * @param {node}        actions    extra footer controls (e.g. a dev verdict)
 */
export default function CaseDetailModal({
  testCase, kind = "exe", projectId, onClose, actions,
}) {
  const [run, setRun] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingRun, setLoadingRun] = useState(false);
  const [loadingCase, setLoadingCase] = useState(false);
  const [error, setError] = useState(null);

  const runId = testCase?.run_id || testCase?.runId || null;
  // A runner item points back at the generated case it was copied from; the
  // project's own list gives the case id directly.
  const caseId = testCase?.test_case_id || testCase?.external_id || testCase?.id || null;

  const loadRun = useCallback(async () => {
    if (!runId) { setRun(null); return; }
    setLoadingRun(true);
    try {
      setRun(await (FETCH_BY_KIND[kind] || getExeRun)(runId));
    } catch (e) {
      setError((e && e.message) || "Could not load this run's steps.");
    } finally {
      setLoadingRun(false);
    }
  }, [runId, kind]);

  useEffect(() => { loadRun(); }, [loadRun]);

  // The rich fields only exist on a generated case, so this is opt-in: the
  // runner catalogs (exe/extension) hold copies without preconditions or steps.
  useEffect(() => {
    if (!projectId || !caseId) { setDetail(null); return undefined; }
    let cancelled = false;
    setLoadingCase(true);
    getProjectTestCase(projectId, caseId)
      .then((d) => { if (!cancelled) setDetail(d); })
      // A missing detail is not an error worth shouting about — the modal still
      // shows everything the row already carried.
      .catch(() => { if (!cancelled) setDetail(null); })
      .finally(() => { if (!cancelled) setLoadingCase(false); });
    return () => { cancelled = true; };
  }, [projectId, caseId]);

  if (!testCase) return null;

  const d = detail || {};
  const name = d.name || testCase.name || testCase.test_scenario
    || (testCase.instruction || "").slice(0, 120) || "Test case";
  const url = d.page_url || testCase.url || testCase.page_url || run?.url;
  const category = d.category || testCase.category;
  const expected = d.expected_result || testCase.expected_result;
  // The stored value is "manual_only", never "manual", so the old equality check
  // was always false and the reason below never rendered. Test against "auto"
  // instead — the same rule the API and the page counts use.
  const manual = String(d.execution_mode || testCase.execution_mode || "auto")
    .toLowerCase() !== "auto";
  const summaryText = d.description || d.instruction
    || testCase.test_description || testCase.test_scenario || testCase.instruction || "";
  // Prefer the generated case, fall back to the runner's copy of it. Before this
  // a case opened from a batch report showed no preconditions, no steps and no
  // test data — every one of them was on the row, just never read.
  const preconditions = d.preconditions?.length ? d.preconditions : lines(testCase.preconditions);
  const steps = d.steps?.length ? d.steps : lines(testCase.instruction);
  const testData = d.test_data?.length ? d.test_data : lines(testCase.test_data);

  // Which step indexes actually have a screenshot on disk. Nothing writes a
  // screenshot path back onto a step, so before this the modal found none —
  // hundreds of captured frames existed and not one was reachable.
  const shotIndexes = (run?.screenshots || []).map(Number);

  // exe, extension and cloud spell the same facts differently (success/status,
  // detail/reason, has_shot/has_screenshot). One normaliser, so this file never
  // has to know which runner it is looking at — reading the wrong names is what
  // made a failed run show a column of green ticks and no failure text at all.
  // The RAW list is kept as well: StepTimeline normalizes internally, and
  // normalizing twice silently empties `text` (the failure narrative) and
  // `inputValue`, because the second pass looks for `reason`/`detail` on an
  // object that by then only carries `text`. `runSteps` is for this file's own
  // reads (counts, failedSteps); the timeline gets `rawSteps`.
  const rawSteps = run?.steps || run?.step_results || [];
  const runSteps = normalizeSteps(rawSteps, shotIndexes);
  const bug = run?.bug_report || run?.bug || null;

  const stepIndexes = new Set(runSteps.map((s) => s.index));
  // Frames captured against a step the run never listed. Rare, but dropping them
  // silently would be the same mistake again — evidence you have and don't show.
  const orphanShots = shotIndexes.filter((n) => !stepIndexes.has(n));

  // The account of WHY it failed. Each runner keeps this somewhere different:
  // exe writes it into bug_report.reason, the extension leaves it on the last
  // failing step, and the batch row carries `last_error` as a one-liner.
  // Whichever exists, the reader needs it — the developer opening this modal is
  // here to find out what broke.
  const failed_ = failedSteps(runSteps);
  const stepNarrative = [...failed_].reverse()
    .map((s) => s.text)
    .find((t) => t && t.length > 0) || "";
  const failed = ["failed", "error", "errored"].includes(
    String(testCase.status || run?.status || "").toLowerCase(),
  );

  // Prefer a step that failed AND has a frame; otherwise any step with one — a
  // run usually shoots its final state even when no single step is marked.
  const evidenceStep = failed_.find((s) => s.hasShot) || runSteps.find((s) => s.hasShot);

  // One sentence for the panel above: the fullest true account available.
  const bugNarrative = String(
    bug?.actual || bug?.failed_step?.detail || bug?.reason || "",
  ).trim();
  const headline = bugNarrative || stepNarrative || String(testCase.last_error || "").trim();
  // Which step gave way, in words — "step 4 of 6 · clicked Subscribe" beats an
  // index, and it is the first thing someone reaching for the timeline wants.
  const failedStep = failed_[0];
  const whereFailed = [
    failedStep
      ? `Step ${Number(failedStep.index) + 1} of ${runSteps.length}`
      : (bug?.failed_step?.action ? `The “${bug.failed_step.action}” step` : ""),
    failedStep?.action || "",
  ].filter(Boolean).join(" · ");

  // The panel above covers expected / actual / where / evidence. The card below
  // is worth keeping only for what the panel can't show — the reproduction
  // steps a cloud run files. Anything else would be the same text twice.
  const reproSteps = bug?.repro || bug?.steps_to_reproduce || [];
  const showBugCard = reproSteps.length > 0;


  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={name}
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">{actions}</div>
          <div className="flex gap-2">
            {runId && (
              <Button variant="outline" size="sm" onClick={loadRun} disabled={loadingRun}>
                <RotateCw size={15} className={loadingRun ? "animate-spin" : ""} /> Refresh
              </Button>
            )}
            <Button size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        {/* what kind of case this is, at a glance */}
        <div className="flex flex-wrap items-center gap-2">
          {(testCase.status || run?.status) && (
            <StatusBadge status={testCase.status || run?.status} />
          )}
          {category && (
            <Badge tone="sky" className="capitalize">
              {String(category).replace(/_/g, " ")}
            </Badge>
          )}
          {(d.priority || testCase.priority) && (
            <Badge tone="slate" className="capitalize">
              {d.priority || testCase.priority} priority
            </Badge>
          )}
          {d.module && <Badge tone="slate">{d.module}</Badge>}
          {manual && (
            <Badge tone="amber"><Hand size={11} /> manual only</Badge>
          )}
          {d.account && (
            <Badge tone="brand"><UserIcon size={11} /> {d.account}</Badge>
          )}
          {d.mutates_data && (
            <Badge tone="rose"><ShieldAlert size={11} /> changes data</Badge>
          )}
          {testCase.dev_status && (
            <Badge tone="slate" className="capitalize">
              dev: {testCase.dev_status}
            </Badge>
          )}
          {/* No cost / tokens / model / duration here. This modal answers "what
              broke and where", and run economics are noise against that — they
              stay on the runner pages that exist to report them. */}
        </div>

        {manual && d.manual_reason && (
          <p className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
            <Hand size={15} className="mt-0.5 shrink-0" />
            <span>This one can&apos;t be automated — {d.manual_reason}</span>
          </p>
        )}

        {/* The answer to "what am I looking at", before anything else.
            A developer opens this to find out what broke; making them read a
            step timeline to work it out is making them do the tool's job. */}
        {/* A bug report is worth showing on its own; a bare narrative only when
            the case actually failed, or a passing case would sprout a red box
            over an incidental step message. */}
        {(bug || (failed && (stepNarrative || testCase.last_error))) && (
          <div className="overflow-hidden rounded-2xl border border-rose-200 dark:border-rose-500/25">
            <p className="flex items-center justify-between gap-2 bg-rose-500/10 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-300">
              <span className="flex items-center gap-2"><Bug size={14} /> What went wrong</span>
              {bug?.severity && <SeverityTag severity={bug.severity} />}
            </p>
            <dl className="divide-y divide-rose-100 dark:divide-rose-500/15">
              {expected && (
                <div className="px-4 py-3">
                  <dt className="text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-400">
                    It should have
                  </dt>
                  <dd className="mt-0.5 text-sm text-slate-700 dark:text-slate-200">{expected}</dd>
                </div>
              )}
              <div className="px-4 py-3">
                <dt className="text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-400">
                  Instead
                </dt>
                <dd className="mt-0.5 whitespace-pre-line text-sm text-slate-700 dark:text-slate-200">
                  {headline}
                </dd>
              </div>
              {whereFailed && (
                <div className="px-4 py-3">
                  <dt className="text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-400">
                    Where
                  </dt>
                  <dd className="mt-0.5 text-sm text-slate-700 dark:text-slate-200">{whereFailed}</dd>
                </div>
              )}
            </dl>
            {evidenceStep && (
              <div className="border-t border-rose-100 px-4 py-3 dark:border-rose-500/15">
                <p className="mb-1.5 text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-400">
                  Screen at the moment it failed
                </p>
                <ScreenshotViewer runId={runId} stepIndex={evidenceStep.index} kind={kind} />
              </div>
            )}
          </div>
        )}

        {loadingCase && !detail && (
          <div className="flex justify-center py-4 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
          </div>
        )}

        {/* the case itself */}
        <Section icon={ListChecks} title="What this test does" show={!!summaryText}>
          <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">
            {summaryText}
          </p>
        </Section>

        <Section icon={ClipboardList} title="Preconditions"
                 show={preconditions.length > 0}>
          <Bullets items={preconditions} />
        </Section>

        {/* Steps are the one field worth calling out when missing: a case with
            no steps is under-specified, not merely inapplicable. */}
        <Section
          icon={ListChecks}
          title="Steps"
          show={steps.length > 0}
          empty={detail && !steps.length ? "No steps were authored for this case." : undefined}
        >
          <Bullets items={steps} numbered />
        </Section>

        <Section icon={Database} title="Test data" show={testData.length > 0}>
          <Bullets items={testData} />
        </Section>

        <Section icon={Target} title="Expected result" show={!!expected}>
          <p className="rounded-xl bg-emerald-50/70 px-4 py-3 text-sm text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-200">
            {expected}
          </p>
        </Section>

        {/* where and which */}
        <div className="grid gap-3 rounded-2xl border border-slate-200 p-4 text-sm dark:border-white/10 sm:grid-cols-2">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-400">
              <Globe size={12} /> Page
            </p>
            {url ? (
              <a href={url} target="_blank" rel="noreferrer"
                 className="mt-0.5 block break-all font-mono text-xs text-brand-600 hover:underline dark:text-brand-300">
                {url}
              </a>
            ) : <p className="mt-0.5 text-slate-400">—</p>}
          </div>
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-400">
              <Hash size={12} /> Case ID
            </p>
            <p className="mt-0.5 break-all font-mono text-xs text-slate-500">{caseId || "—"}</p>
          </div>
        </div>

        {testCase.dev_note && (
          <Section icon={UserIcon} title="Developer's note" show>
            <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:bg-white/5 dark:text-slate-200">
              {testCase.dev_note}
            </p>
          </Section>
        )}

        {/* The row's one-line error. Suppressed once the report below says the
            same thing at length — printing both made the modal look like it had
            found two separate problems. */}
        {testCase.last_error && !bug && !stepNarrative && (
          <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
            {testCase.last_error}
          </p>
        )}

        {/* what happened when it ran — secondary, and only once there's a run */}
        {error && (
          <p className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" /> {error}
          </p>
        )}

        {runId && (
          <div className="border-t border-slate-200 pt-5 dark:border-white/10">
            {loadingRun ? (
              <div className="flex justify-center py-6 text-slate-400">
                <Loader2 size={20} className="animate-spin" />
              </div>
            ) : (
              <>
                {/* Only when the report adds something the panel at the top
                    hasn't already said. Both were rendering the same paragraph
                    and the same screenshot, which reads as two findings rather
                    than one told twice. Repro steps are the card's own. */}
                {showBugCard && (
                  <BugReportCard
                    bug={bug}
                    fallbackNarrative={stepNarrative}
                    kind={kind}
                  />
                )}
                {runSteps.length > 0 && (
                  <div className={cn(showBugCard && "mt-5")}>
                    <h4 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      <CheckCircle2 size={13} /> What happened when it ran ({runSteps.length} steps)
                    </h4>
                    <StepTimeline
                      steps={rawSteps} runId={runId} kind={kind} shotIndexes={shotIndexes}
                    />
                  </div>
                )}

                {orphanShots.length > 0 && (
                  <div className="mt-5">
                    <h4 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      <ImageIcon size={13} /> Other screenshots ({orphanShots.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {orphanShots.map((n) => (
                        <ScreenshotViewer
                          key={n} runId={runId} stepIndex={n} kind={kind}
                          label={`step ${n + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!runId && (
          <p className="rounded-xl border border-dashed border-slate-200 px-4 py-3 text-center text-xs text-slate-400 dark:border-white/10">
            Not run yet — once it has, the steps it took and the screenshots appear here.
          </p>
        )}
      </div>
    </Modal>
  );
}
